/**
 * Pure state model for the editor workbench layout.
 *
 * The model deliberately stores tab identities rather than tab descriptors.  A
 * descriptor (and any state owned by a descriptor) can therefore remain in the
 * client while this module only changes placement, focus, and selection.
 */

export const EDITOR_FILE_TAB_PREFIX = "file:" as const;
export const EDITOR_TAB_DRAG_MIME =
  "application/x-workerstation-editor-tab" as const;
export const EDITOR_TAB_DRAG_VERSION = 1 as const;

export type EditorSplitDirection = "vertical" | "horizontal";
export type EditorAdjacentDirection = "left" | "right" | "up" | "down";

export interface EditorGroupNode {
  readonly kind: "group";
  readonly id: string;
  /** Ordered tab identities owned by this group. */
  readonly tabs: readonly string[];
  /** Always null for an empty group and always one of tabs otherwise. */
  readonly activeTabId: string | null;
  /** Compatibility alias for callers that name the collection tabIds. */
  readonly tabIds?: readonly string[];
}

export interface EditorSplitNode {
  readonly kind: "split";
  readonly id: string;
  /** vertical = side-by-side; horizontal = stacked. */
  readonly direction: EditorSplitDirection;
  /** Percentage occupied by the first child, in the inclusive range 0..100. */
  readonly ratio: number;
  readonly children: readonly [EditorLayoutNode, EditorLayoutNode];
  /** Compatibility accessors for callers that use first/second terminology. */
  readonly first?: EditorLayoutNode;
  readonly second?: EditorLayoutNode;
}

export type EditorLayoutNode = EditorGroupNode | EditorSplitNode;

export interface EditorLayoutState {
  readonly root: EditorLayoutNode;
  readonly focusedGroupId: string;
}

export type EditorLayout = EditorLayoutState;
export type EditorGroup = EditorGroupNode;
export type EditorSplit = EditorSplitNode;

export interface CreateEditorLayoutOptions {
  readonly tabs?: readonly string[];
  readonly tabIds?: readonly string[];
  readonly activeTabId?: string | null;
  readonly rootGroupId?: string;
  /** Alias for rootGroupId. */
  readonly groupId?: string;
  readonly focusedGroupId?: string;
  readonly root?: EditorLayoutNode;
}

export interface ExistingFileTabs {
  readonly openTabs?: readonly string[];
  readonly currentFile?: string | null;
}

export interface SplitGroupOptions {
  readonly groupId?: string;
  readonly direction?: EditorSplitDirection;
  readonly ratio?: number;
  readonly newGroupId?: string;
  readonly splitId?: string;
  /** New groups are focused by default; source keeps focus when set to source. */
  readonly focus?: "new" | "source";
}

export interface OpenTabOptions {
  readonly groupId?: string;
}

export interface ReorderTabOptions {
  readonly groupId?: string;
  readonly tabId: string;
  /** Final zero-based position after removing the tab from its old position. */
  readonly index: number;
}

export interface MoveTabOptions {
  readonly tabId: string;
  readonly targetGroupId: string;
  /** Final zero-based position; omitted means append. */
  readonly targetIndex?: number;
}

export interface EditorTabDragPayload {
  readonly version: typeof EDITOR_TAB_DRAG_VERSION;
  readonly tabId: string;
  readonly sourceGroupId: string;
}

export interface EditorLayoutValidation {
  readonly valid: boolean;
  readonly reason?: string;
}

const DEFAULT_ROOT_GROUP_ID = "group-1";
const DEFAULT_SPLIT_RATIO = 50;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function requiredId(value: unknown, label: string): string {
  if (typeof value !== "string" || value.length === 0) {
    throw new TypeError(`${label} must be a non-empty string.`);
  }
  return value;
}

function requiredTabs(value: unknown, label = "Tab IDs"): string[] {
  if (!Array.isArray(value)) {
    throw new TypeError(`${label} must be an array.`);
  }
  const tabs = value.map((tabId, index) =>
    requiredId(tabId, `${label}[${index}]`)
  );
  if (new Set(tabs).size !== tabs.length) {
    throw new TypeError(`${label} must contain unique tab IDs.`);
  }
  return tabs;
}

function freezeArray<T>(values: readonly T[]): readonly T[] {
  return Object.freeze([...values]);
}

function normalizeRatio(value: unknown, label = "Split ratio"): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new TypeError(`${label} must be finite.`);
  }
  return Math.min(100, Math.max(0, value));
}

function addCompatibilityAlias(
  target: object,
  name: string,
  value: unknown
): void {
  Object.defineProperty(target, name, {
    configurable: false,
    enumerable: false,
    value,
    writable: false
  });
}

function makeGroup(
  id: unknown,
  tabs: readonly string[],
  activeTabId: string | null | undefined
): EditorGroupNode {
  const normalizedId = requiredId(id, "Group ID");
  const normalizedTabs = requiredTabs(tabs);
  const active = activeTabId === undefined || activeTabId === null
    ? (normalizedTabs[0] ?? null)
    : activeTabId;
  if (active !== null) {
    requiredId(active, "Active tab ID");
    if (!normalizedTabs.includes(active)) {
      throw new RangeError("Active tab must belong to its editor group.");
    }
  }
  if (normalizedTabs.length === 0 && active !== null) {
    throw new RangeError("An empty editor group cannot have an active tab.");
  }
  const group = {
    kind: "group" as const,
    id: normalizedId,
    tabs: freezeArray(normalizedTabs),
    activeTabId: active
  };
  addCompatibilityAlias(group, "tabIds", group.tabs);
  return Object.freeze(group);
}

function makeSplit(
  id: unknown,
  direction: unknown,
  ratio: unknown,
  first: EditorLayoutNode,
  second: EditorLayoutNode
): EditorSplitNode {
  const normalizedId = requiredId(id, "Split ID");
  if (direction !== "vertical" && direction !== "horizontal") {
    throw new TypeError("Split direction must be vertical or horizontal.");
  }
  const children = Object.freeze([first, second]) as readonly [
    EditorLayoutNode,
    EditorLayoutNode
  ];
  const split: {
    readonly kind: "split";
    readonly id: string;
    readonly direction: EditorSplitDirection;
    readonly ratio: number;
    readonly children: readonly [EditorLayoutNode, EditorLayoutNode];
  } = {
    kind: "split" as const,
    id: normalizedId,
    direction: direction as EditorSplitDirection,
    ratio: normalizeRatio(ratio),
    children
  };
  addCompatibilityAlias(split, "first", first);
  addCompatibilityAlias(split, "second", second);
  return Object.freeze(split);
}

function makeState(
  root: EditorLayoutNode,
  focusedGroupId: string
): EditorLayoutState {
  return Object.freeze({ root, focusedGroupId });
}

function nodeChildren(node: EditorSplitNode): readonly [EditorLayoutNode, EditorLayoutNode] {
  if (Array.isArray(node.children) && node.children.length === 2) {
    return node.children;
  }
  if (node.first !== undefined && node.second !== undefined) {
    return [node.first, node.second];
  }
  throw new TypeError("Split node must contain exactly two children.");
}

function cloneNode(value: unknown): EditorLayoutNode {
  if (!isRecord(value)) {
    throw new TypeError("Editor layout nodes must be objects.");
  }
  if (value.kind === "group") {
    return makeGroup(
      value.id,
      requiredTabs(value.tabs),
      value.activeTabId as string | null | undefined
    );
  }
  if (value.kind === "split") {
    const children = nodeChildren(value as unknown as EditorSplitNode);
    return makeSplit(
      requiredId(value.id, "Split ID"),
      value.direction,
      value.ratio,
      cloneNode(children[0]),
      cloneNode(children[1])
    );
  }
  throw new TypeError("Editor layout node kind must be group or split.");
}

interface LayoutCollections {
  readonly groups: EditorGroupNode[];
  readonly nodeIds: Set<string>;
  readonly tabIds: Set<string>;
}

function collectNode(
  node: unknown,
  collections: LayoutCollections,
  isRoot = false
): void {
  if (!isRecord(node)) {
    throw new TypeError("Editor layout nodes must be objects.");
  }
  const id = requiredId(node.id, "Layout node ID");
  if (collections.nodeIds.has(id)) {
    throw new TypeError(`Layout node ID is duplicated: ${id}.`);
  }
  collections.nodeIds.add(id);
  if (node.kind === "group") {
    const tabs = requiredTabs(node.tabs);
    const active = node.activeTabId;
    if (tabs.length > 0 && (active === null || active === undefined)) {
      throw new TypeError("A non-empty editor group must have an active tab.");
    }
    if (active !== null && active !== undefined) {
      if (!tabs.includes(requiredId(active, "Active tab ID"))) {
        throw new TypeError("Active tab must belong to its editor group.");
      }
    }
    if (tabs.length === 0 && active !== null && active !== undefined) {
      throw new TypeError("An empty editor group cannot have an active tab.");
    }
    for (const tabId of tabs) {
      if (collections.tabIds.has(tabId)) {
        throw new TypeError(`Tab ID is owned by more than one group: ${tabId}.`);
      }
      collections.tabIds.add(tabId);
    }
    collections.groups.push(node as unknown as EditorGroupNode);
    return;
  }
  if (node.kind !== "split") {
    throw new TypeError("Editor layout node kind must be group or split.");
  }
  if (isRoot && id.length === 0) {
    throw new TypeError("Root layout node ID must be non-empty.");
  }
  if (node.direction !== "vertical" && node.direction !== "horizontal") {
    throw new TypeError("Split direction must be vertical or horizontal.");
  }
  normalizeRatio(node.ratio);
  const children = nodeChildren(node as unknown as EditorSplitNode);
  collectNode(children[0], collections);
  collectNode(children[1], collections);
}

function collectLayout(root: unknown): LayoutCollections {
  const collections: LayoutCollections = {
    groups: [],
    nodeIds: new Set<string>(),
    tabIds: new Set<string>()
  };
  collectNode(root, collections, true);
  if (collections.groups.length === 0) {
    throw new TypeError("An editor layout must contain at least one group.");
  }
  return collections;
}

function assertLayoutValue(value: unknown): asserts value is EditorLayoutState {
  if (!isRecord(value)) {
    throw new TypeError("Editor layout state must be an object.");
  }
  const collections = collectLayout(value.root);
  const focusedGroupId = requiredId(value.focusedGroupId, "Focused group ID");
  if (!collections.groups.some((group) => group.id === focusedGroupId)) {
    throw new RangeError("Focused group must exist in the editor layout.");
  }
}

/** Throw if a state does not satisfy all layout ownership and selection invariants. */
export function assertValidEditorLayout(
  value: unknown
): asserts value is EditorLayoutState {
  assertLayoutValue(value);
}

/** Alias with the shorter name used by model callers. */
export const assertEditorLayout = assertValidEditorLayout;

export function validateEditorLayout(value: unknown): EditorLayoutValidation {
  try {
    assertLayoutValue(value);
    return Object.freeze({ valid: true });
  } catch (error) {
    return Object.freeze({
      valid: false,
      reason: error instanceof Error ? error.message : String(error)
    });
  }
}

export function isEditorLayout(value: unknown): value is EditorLayoutState {
  return validateEditorLayout(value).valid;
}

function groupsInNode(node: EditorLayoutNode): EditorGroupNode[] {
  if (node.kind === "group") return [node];
  return [
    ...groupsInNode(node.children[0]),
    ...groupsInNode(node.children[1])
  ];
}

function firstGroupId(node: EditorLayoutNode): string {
  return node.kind === "group" ? node.id : firstGroupId(node.children[0]);
}

function focusedGroupAfterCollapse(
  root: EditorLayoutNode,
  removedPath: readonly PathEntry[]
): string {
  for (let index = removedPath.length - 1; index >= 0; index -= 1) {
    const entry = removedPath[index];
    const sibling = entry.split.children[entry.childIndex === 0 ? 1 : 0];
    const candidate = firstGroupId(sibling);
    if (findGroupInNode(root, candidate)) return candidate;
  }
  return firstGroupId(root);
}

function findGroupInNode(
  node: EditorLayoutNode,
  groupId: string
): EditorGroupNode | undefined {
  if (node.kind === "group") return node.id === groupId ? node : undefined;
  return (
    findGroupInNode(node.children[0], groupId) ??
    findGroupInNode(node.children[1], groupId)
  );
}

function findNodeById(
  node: EditorLayoutNode,
  nodeId: string
): EditorLayoutNode | undefined {
  if (node.id === nodeId) return node;
  if (node.kind === "group") return undefined;
  return (
    findNodeById(node.children[0], nodeId) ??
    findNodeById(node.children[1], nodeId)
  );
}

function findTabGroupInNode(
  node: EditorLayoutNode,
  tabId: string
): string | undefined {
  if (node.kind === "group") {
    return node.tabs.includes(tabId) ? node.id : undefined;
  }
  return (
    findTabGroupInNode(node.children[0], tabId) ??
    findTabGroupInNode(node.children[1], tabId)
  );
}

function replaceNode(
  node: EditorLayoutNode,
  targetId: string,
  replacement: (node: EditorLayoutNode) => EditorLayoutNode
): [EditorLayoutNode, boolean] {
  if (node.id === targetId) return [replacement(node), true];
  if (node.kind === "group") return [node, false];
  const [first, firstChanged] = replaceNode(node.children[0], targetId, replacement);
  if (firstChanged) {
    return [
      makeSplit(node.id, node.direction, node.ratio, first, node.children[1]),
      true
    ];
  }
  const [second, secondChanged] = replaceNode(node.children[1], targetId, replacement);
  if (secondChanged) {
    return [
      makeSplit(node.id, node.direction, node.ratio, node.children[0], second),
      true
    ];
  }
  return [node, false];
}

function replaceGroups(
  node: EditorLayoutNode,
  updates: ReadonlyMap<string, EditorGroupNode>
): [EditorLayoutNode, boolean] {
  if (node.kind === "group") {
    const next = updates.get(node.id);
    return next === undefined ? [node, false] : [next, true];
  }
  const [first, firstChanged] = replaceGroups(node.children[0], updates);
  const [second, secondChanged] = replaceGroups(node.children[1], updates);
  if (!firstChanged && !secondChanged) return [node, false];
  return [
    makeSplit(
      node.id,
      node.direction,
      node.ratio,
      firstChanged ? first : node.children[0],
      secondChanged ? second : node.children[1]
    ),
    true
  ];
}

function stateWithRoot(
  state: EditorLayoutState,
  root: EditorLayoutNode,
  focusedGroupId = state.focusedGroupId
): EditorLayoutState {
  const next = makeState(root, focusedGroupId);
  assertLayoutValue(next);
  return next;
}

function requireGroup(
  state: EditorLayoutState,
  groupId: string
): EditorGroupNode {
  requiredId(groupId, "Group ID");
  const group = findGroupInNode(state.root, groupId);
  if (!group) throw new RangeError(`Editor group does not exist: ${groupId}.`);
  return group;
}

function requireTabId(tabId: unknown): string {
  return requiredId(tabId, "Tab ID");
}

function fallbackAfterRemoval(
  tabs: readonly string[],
  removedIndex: number
): string | null {
  const remaining = tabs.filter((_, index) => index !== removedIndex);
  return remaining[removedIndex] ?? remaining[removedIndex - 1] ?? null;
}

function integerIndex(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isInteger(value)) {
    throw new TypeError(`${label} must be an integer.`);
  }
  return value;
}

function clampInsertionIndex(index: number, length: number): number {
  return Math.max(0, Math.min(index, length));
}

function nextNodeId(root: EditorLayoutNode, prefix: "group" | "split"): string {
  const used = collectLayout(root).nodeIds;
  for (let number = 1; ; number += 1) {
    const candidate = `${prefix}-${number}`;
    if (!used.has(candidate)) return candidate;
  }
}

function ensureNewNodeId(
  root: EditorLayoutNode,
  requested: string | undefined,
  prefix: "group" | "split"
): string {
  const candidate = requested === undefined
    ? nextNodeId(root, prefix)
    : requiredId(requested, `${prefix === "group" ? "Group" : "Split"} ID`);
  if (collectLayout(root).nodeIds.has(candidate)) {
    throw new RangeError(`Layout node ID is already in use: ${candidate}.`);
  }
  return candidate;
}

/** Construct a frozen editor-group leaf. */
export function createEditorGroup(
  id: string,
  tabs: readonly string[] = [],
  activeTabId?: string | null
): EditorGroupNode {
  return makeGroup(id, tabs, activeTabId);
}

/** Construct a frozen split node; use createEditorLayoutState to add focus. */
export function createEditorSplit(
  id: string,
  direction: EditorSplitDirection,
  ratio: number,
  first: EditorLayoutNode,
  second: EditorLayoutNode
): EditorSplitNode {
  const normalizedFirst = cloneNode(first);
  const normalizedSecond = cloneNode(second);
  const node = makeSplit(id, direction, ratio, normalizedFirst, normalizedSecond);
  const collections: LayoutCollections = {
    groups: [],
    nodeIds: new Set<string>(),
    tabIds: new Set<string>()
  };
  collectNode(node, collections, true);
  if (collections.groups.length === 0) {
    throw new TypeError("A split must contain at least one editor group.");
  }
  return node;
}

/** Create state from an already-shaped recursive root. */
export function createEditorLayoutState(
  root: EditorLayoutNode,
  focusedGroupId?: string
): EditorLayoutState {
  const normalizedRoot = cloneNode(root);
  const groups = groupsInNode(normalizedRoot);
  const focused = focusedGroupId ?? groups[0]?.id;
  if (focused === undefined) {
    throw new TypeError("An editor layout must contain at least one group.");
  }
  requiredId(focused, "Focused group ID");
  const state = makeState(normalizedRoot, focused);
  assertLayoutValue(state);
  return state;
}

/**
 * Create a one-group layout.  The array overload is convenient for tests and
 * the options overload leaves room for a compatibility-friendly root ID.
 */
export function createEditorLayout(
  input?: readonly string[] | CreateEditorLayoutOptions | EditorLayoutNode,
  activeTabId?: string | null
): EditorLayoutState {
  if (Array.isArray(input)) {
    const tabs = requiredTabs(input);
    const active = activeTabId === undefined ? (tabs[0] ?? null) : activeTabId;
    return createEditorLayoutState(
      makeGroup(DEFAULT_ROOT_GROUP_ID, tabs, active),
      DEFAULT_ROOT_GROUP_ID
    );
  }
  if (isRecord(input) && (input.kind === "group" || input.kind === "split")) {
    return createEditorLayoutState(input as unknown as EditorLayoutNode, activeTabId ?? undefined);
  }
  if (input !== undefined && !isRecord(input)) {
    throw new TypeError("Editor layout input must be tabs, options, or a layout node.");
  }
  const options = (input ?? {}) as CreateEditorLayoutOptions;
  if (options.root !== undefined) {
    return createEditorLayoutState(options.root, options.focusedGroupId);
  }
  const tabs = requiredTabs(options.tabs ?? options.tabIds ?? []);
  const rootGroupId = options.rootGroupId ?? options.groupId ?? DEFAULT_ROOT_GROUP_ID;
  const active = options.activeTabId !== undefined
    ? options.activeTabId
    : activeTabId === undefined
      ? (tabs[0] ?? null)
      : activeTabId;
  return createEditorLayoutState(
    makeGroup(rootGroupId, tabs, active),
    options.focusedGroupId ?? rootGroupId
  );
}

function compatibilityFileTabs(
  input: readonly string[] | ExistingFileTabs,
  currentFile?: string | null
): { tabs: string[]; activeTabId: string | null } {
  const openTabs = Array.isArray(input)
    ? input
    : isRecord(input)
      ? input.openTabs ?? []
      : undefined;
  if (!Array.isArray(openTabs)) {
    throw new TypeError("Existing openTabs must be an array.");
  }
  const compatibility = Array.isArray(input)
    ? undefined
    : input as ExistingFileTabs;
  const requestedCurrent = Array.isArray(input)
    ? currentFile
    : compatibility?.currentFile === undefined
      ? currentFile
      : compatibility.currentFile;
  if (requestedCurrent !== undefined && requestedCurrent !== null) {
    requiredId(requestedCurrent, "Current file");
  }
  const paths: string[] = [];
  const seen = new Set<string>();
  for (const path of openTabs) {
    requiredId(path, "Existing file tab path");
    if (seen.has(path)) continue;
    seen.add(path);
    paths.push(path);
  }
  if (requestedCurrent !== undefined && requestedCurrent !== null && !seen.has(requestedCurrent)) {
    paths.push(requestedCurrent);
  }
  const tabs = paths.map(fileEditorTabId);
  const activeTabId = requestedCurrent === undefined || requestedCurrent === null
    ? (tabs[0] ?? null)
    : fileEditorTabId(requestedCurrent);
  return { tabs, activeTabId };
}

/** Initialize one valid group from the pre-grouped persisted file-tab fields. */
export function initializeEditorLayout(
  input: readonly string[] | ExistingFileTabs = [],
  currentFile?: string | null
): EditorLayoutState {
  const compatibility = compatibilityFileTabs(input, currentFile);
  return createEditorLayout({
    tabs: compatibility.tabs,
    activeTabId: compatibility.activeTabId
  });
}

export const createEditorLayoutFromFileTabs = initializeEditorLayout;

/** Return the stable identity used by an existing file tab. */
export function fileEditorTabId(path: string): string {
  return `${EDITOR_FILE_TAB_PREFIX}${requiredId(path, "File path")}`;
}

export const fileTabId = fileEditorTabId;

export function editorGroups(state: EditorLayoutState): readonly EditorGroupNode[] {
  assertLayoutValue(state);
  return freezeArray(groupsInNode(state.root));
}

export const getEditorGroups = editorGroups;

export function findEditorGroup(
  state: EditorLayoutState,
  groupId: string
): EditorGroupNode | undefined {
  assertLayoutValue(state);
  requiredId(groupId, "Group ID");
  return findGroupInNode(state.root, groupId);
}

export const findGroup = findEditorGroup;

export function findEditorTabGroup(
  state: EditorLayoutState,
  tabId: string
): string | undefined {
  assertLayoutValue(state);
  return findTabGroupInNode(state.root, requireTabId(tabId));
}

export const findTabGroup = findEditorTabGroup;

export function findEditorSplit(
  state: EditorLayoutState,
  splitId: string
): EditorSplitNode | undefined {
  assertLayoutValue(state);
  requiredId(splitId, "Split ID");
  const node = findNodeById(state.root, splitId);
  return node?.kind === "split" ? node : undefined;
}

/** Focus a group without changing any tab placement or selection. */
export function focusGroup(
  state: EditorLayoutState,
  groupId: string
): EditorLayoutState {
  assertLayoutValue(state);
  requireGroup(state, groupId);
  return state.focusedGroupId === groupId
    ? state
    : makeState(state.root, groupId);
}

export const focusEditorGroup = focusGroup;

function resolveSelection(
  state: EditorLayoutState,
  first: string,
  second: string | undefined
): { groupId: string; tabId: string } | undefined {
  if (second === undefined) {
    const tabId = requireTabId(first);
    const groupId = findTabGroupInNode(state.root, tabId);
    return groupId === undefined ? undefined : { groupId, tabId };
  }
  requiredId(first, "Group or tab ID");
  requiredId(second, "Tab or group ID");
  if (findGroupInNode(state.root, first)) {
    return { groupId: first, tabId: second };
  }
  if (findGroupInNode(state.root, second)) {
    return { groupId: second, tabId: first };
  }
  throw new RangeError("Neither selection argument identifies an editor group.");
}

/** Select a tab in a group, or locate its owning group with the two-argument form. */
export function selectTab(
  state: EditorLayoutState,
  tabId: string
): EditorLayoutState;
export function selectTab(
  state: EditorLayoutState,
  groupId: string,
  tabId: string
): EditorLayoutState;
export function selectTab(
  state: EditorLayoutState,
  first: string,
  second?: string
): EditorLayoutState {
  assertLayoutValue(state);
  const selection = resolveSelection(state, first, second);
  if (selection === undefined) return state;
  const group = requireGroup(state, selection.groupId);
  if (!group.tabs.includes(selection.tabId)) {
    throw new RangeError("Selected tab does not belong to the requested group.");
  }
  if (
    state.focusedGroupId === group.id &&
    group.activeTabId === selection.tabId
  ) {
    return state;
  }
  const [root] = replaceNode(state.root, group.id, (node) => {
    if (node.kind !== "group") return node;
    return makeGroup(node.id, node.tabs, selection.tabId);
  });
  return stateWithRoot(state, root, group.id);
}

export const selectEditorTab = selectTab;

/** Open a unique tab in the focused group, or focus its existing owner. */
export function openOrFocusTab(
  state: EditorLayoutState,
  tabId: string,
  options?: OpenTabOptions | string
): EditorLayoutState {
  assertLayoutValue(state);
  const id = requireTabId(tabId);
  const existingGroupId = findTabGroupInNode(state.root, id);
  if (existingGroupId !== undefined) {
    const group = requireGroup(state, existingGroupId);
    const [root] = replaceNode(state.root, existingGroupId, (node) => {
      if (node.kind !== "group" || node.activeTabId === id) return node;
      return makeGroup(node.id, node.tabs, id);
    });
    return stateWithRoot(state, root, existingGroupId);
  }
  const targetGroupId = typeof options === "string"
    ? options
    : options?.groupId ?? state.focusedGroupId;
  const group = requireGroup(state, targetGroupId);
  const [root] = replaceNode(state.root, group.id, (node) => {
    if (node.kind !== "group") return node;
    return makeGroup(node.id, [...node.tabs, id], id);
  });
  return stateWithRoot(state, root, group.id);
}

export const openOrFocusSingletonTab = openOrFocusTab;
export const openTab = openOrFocusTab;

/** Close one tab and select the next tab, then the previous tab, when needed. */
export function closeTab(
  state: EditorLayoutState,
  tabId: string,
  groupId?: string
): EditorLayoutState {
  assertLayoutValue(state);
  const id = requireTabId(tabId);
  const owner = findTabGroupInNode(state.root, id);
  if (owner === undefined || (groupId !== undefined && owner !== groupId)) {
    return state;
  }
  const group = requireGroup(state, owner);
  const index = group.tabs.indexOf(id);
  const nextTabs = group.tabs.filter((candidate) => candidate !== id);
  const nextActive = group.activeTabId === id
    ? fallbackAfterRemoval(group.tabs, index)
    : group.activeTabId;
  const [root] = replaceNode(state.root, owner, (node) => {
    if (node.kind !== "group") return node;
    return makeGroup(node.id, nextTabs, nextActive);
  });
  return stateWithRoot(state, root);
}

export const closeEditorTab = closeTab;

/** Split a focused group, or pass a group ID before the direction. */
export function splitGroup(
  state: EditorLayoutState,
  direction: EditorSplitDirection,
  options?: Omit<SplitGroupOptions, "direction" | "groupId">
): EditorLayoutState;
export function splitGroup(
  state: EditorLayoutState,
  groupId: string,
  direction: EditorSplitDirection,
  options?: Omit<SplitGroupOptions, "direction" | "groupId">
): EditorLayoutState;
export function splitGroup(
  state: EditorLayoutState,
  options: SplitGroupOptions & { readonly direction: EditorSplitDirection }
): EditorLayoutState;
export function splitGroup(
  state: EditorLayoutState,
  first: string | SplitGroupOptions,
  second?: string | Omit<SplitGroupOptions, "direction" | "groupId">,
  third?: Omit<SplitGroupOptions, "direction" | "groupId">
): EditorLayoutState {
  assertLayoutValue(state);
  let groupId = state.focusedGroupId;
  let direction: EditorSplitDirection;
  let options: SplitGroupOptions = {};
  if (typeof first === "object") {
    options = first;
    direction = options.direction as EditorSplitDirection;
    groupId = options.groupId ?? groupId;
  } else if (first === "vertical" || first === "horizontal") {
    direction = first;
    if (second !== undefined && typeof second === "object") options = second;
  } else {
    groupId = first;
    if (typeof second !== "string") {
      throw new TypeError("Split direction must be vertical or horizontal.");
    }
    direction = second as EditorSplitDirection;
    options = third ?? {};
  }
  if (direction !== "vertical" && direction !== "horizontal") {
    throw new TypeError("Split direction must be vertical or horizontal.");
  }
  const source = requireGroup(state, groupId);
  const newGroupId = ensureNewNodeId(state.root, options.newGroupId, "group");
  const splitId = ensureNewNodeId(state.root, options.splitId, "split");
  if (splitId === newGroupId) {
    throw new RangeError(`Layout node ID is already in use: ${splitId}.`);
  }
  const empty = makeGroup(newGroupId, [], null);
  const split = makeSplit(
    splitId,
    direction,
    options.ratio === undefined ? DEFAULT_SPLIT_RATIO : options.ratio,
    source,
    empty
  );
  const [root, changed] = replaceNode(state.root, source.id, () => split);
  if (!changed) throw new RangeError(`Editor group does not exist: ${source.id}.`);
  const focused = options.focus === "source" ? source.id : empty.id;
  return stateWithRoot(state, root, focused);
}

export const splitEditorGroup = splitGroup;

/** Update one recursive split ratio (percent of its first child). */
export function setSplitRatio(
  state: EditorLayoutState,
  splitId: string,
  ratio: number
): EditorLayoutState {
  assertLayoutValue(state);
  requiredId(splitId, "Split ID");
  const split = findNodeById(state.root, splitId);
  if (!split || split.kind !== "split") {
    throw new RangeError(`Editor split does not exist: ${splitId}.`);
  }
  const nextRatio = normalizeRatio(ratio);
  if (split.ratio === nextRatio) return state;
  const [root] = replaceNode(state.root, splitId, (node) => {
    if (node.kind !== "split") return node;
    return makeSplit(node.id, node.direction, nextRatio, node.children[0], node.children[1]);
  });
  return stateWithRoot(state, root);
}

export const updateSplitRatio = setSplitRatio;
export const setEditorSplitRatio = setSplitRatio;

interface PathEntry {
  readonly split: EditorSplitNode;
  readonly childIndex: 0 | 1;
}

function pathToGroup(
  node: EditorLayoutNode,
  groupId: string,
  path: readonly PathEntry[] = []
): readonly PathEntry[] | undefined {
  if (node.kind === "group") return node.id === groupId ? path : undefined;
  const first = pathToGroup(node.children[0], groupId, [
    ...path,
    { split: node, childIndex: 0 }
  ]);
  return first ?? pathToGroup(node.children[1], groupId, [
    ...path,
    { split: node, childIndex: 1 }
  ]);
}

function leafGroupIds(node: EditorLayoutNode): string[] {
  return node.kind === "group"
    ? [node.id]
    : [...leafGroupIds(node.children[0]), ...leafGroupIds(node.children[1])];
}

/**
 * Find groups sharing a layout boundary with a group.  With no direction,
 * results are ordered left, right, up, down and nearest ancestors first.
 */
export function findAdjacentGroups(
  state: EditorLayoutState,
  groupId: string,
  direction?: EditorAdjacentDirection
): readonly string[] {
  assertLayoutValue(state);
  requireGroup(state, groupId);
  if (
    direction !== undefined &&
    direction !== "left" &&
    direction !== "right" &&
    direction !== "up" &&
    direction !== "down"
  ) {
    throw new TypeError("Adjacent direction is invalid.");
  }
  const path = pathToGroup(state.root, groupId);
  if (!path) return freezeArray([]);
  const adjacent: Record<EditorAdjacentDirection, string[]> = {
    left: [],
    right: [],
    up: [],
    down: []
  };
  const add = (side: EditorAdjacentDirection, ids: readonly string[]) => {
    for (const id of ids) {
      if (id !== groupId && !adjacent[side].includes(id)) adjacent[side].push(id);
    }
  };
  for (let index = path.length - 1; index >= 0; index -= 1) {
    const entry = path[index];
    const sibling = entry.split.children[entry.childIndex === 0 ? 1 : 0];
    const ids = leafGroupIds(sibling);
    const ordered = entry.childIndex === 1 ? [...ids].reverse() : ids;
    if (entry.split.direction === "vertical") {
      add(entry.childIndex === 0 ? "right" : "left", ordered);
    } else {
      add(entry.childIndex === 0 ? "down" : "up", ordered);
    }
  }
  if (direction !== undefined) return freezeArray(adjacent[direction]);
  return freezeArray([
    ...adjacent.left,
    ...adjacent.right,
    ...adjacent.up,
    ...adjacent.down
  ]);
}

export function adjacentGroupsByDirection(
  state: EditorLayoutState,
  groupId: string
): Readonly<Record<EditorAdjacentDirection, readonly string[]>> {
  return Object.freeze({
    left: findAdjacentGroups(state, groupId, "left"),
    right: findAdjacentGroups(state, groupId, "right"),
    up: findAdjacentGroups(state, groupId, "up"),
    down: findAdjacentGroups(state, groupId, "down")
  });
}

export function findAdjacentGroup(
  state: EditorLayoutState,
  groupId: string,
  direction?: EditorAdjacentDirection
): string | undefined {
  return findAdjacentGroups(state, groupId, direction)[0];
}

/** Remove an explicitly empty non-root group and collapse its parent split. */
export function collapseEmptyGroup(
  state: EditorLayoutState,
  groupId: string
): EditorLayoutState {
  assertLayoutValue(state);
  const group = requireGroup(state, groupId);
  if (group.tabs.length !== 0) {
    throw new RangeError("Only empty editor groups can be collapsed.");
  }
  if (state.root.kind === "group" && state.root.id === groupId) {
    return state;
  }

  function remove(
    node: EditorLayoutNode
  ): [EditorLayoutNode, boolean] {
    if (node.kind === "group") return [node, false];
    if (node.children[0].kind === "group" && node.children[0].id === groupId) {
      return [node.children[1], true];
    }
    if (node.children[1].kind === "group" && node.children[1].id === groupId) {
      return [node.children[0], true];
    }
    const [first, firstChanged] = remove(node.children[0]);
    if (firstChanged) {
      return [makeSplit(node.id, node.direction, node.ratio, first, node.children[1]), true];
    }
    const [second, secondChanged] = remove(node.children[1]);
    if (secondChanged) {
      return [makeSplit(node.id, node.direction, node.ratio, node.children[0], second), true];
    }
    return [node, false];
  }

  const removedPath = pathToGroup(state.root, groupId);
  if (!removedPath) {
    throw new RangeError(`Editor group does not exist: ${groupId}.`);
  }
  const [root, changed] = remove(state.root);
  if (!changed) throw new RangeError(`Editor group does not exist: ${groupId}.`);
  const focused = state.focusedGroupId === groupId
    ? focusedGroupAfterCollapse(root, removedPath)
    : state.focusedGroupId;
  return stateWithRoot(state, root, focused);
}

export const closeEmptyGroup = collapseEmptyGroup;
export const collapseEditorGroup = collapseEmptyGroup;

function resolveReorder(
  state: EditorLayoutState,
  first: string | ReorderTabOptions,
  second: string | number | undefined,
  third: number | undefined
): { groupId: string; tabId: string; index: number } {
  if (isRecord(first)) {
    const tabId = requireTabId(first.tabId);
    const groupId = first.groupId ?? findTabGroupInNode(state.root, tabId);
    if (groupId === undefined) throw new RangeError(`Tab is not open: ${tabId}.`);
    return {
      groupId: requiredId(groupId, "Group ID"),
      tabId,
      index: integerIndex(first.index, "Tab index")
    };
  }
  const firstId = requireTabId(first);
  if (typeof second === "number") {
    const groupId = findTabGroupInNode(state.root, firstId);
    if (groupId === undefined) throw new RangeError(`Tab is not open: ${firstId}.`);
    return { groupId, tabId: firstId, index: integerIndex(second, "Tab index") };
  }
  if (second === undefined || third === undefined) {
    throw new TypeError("Reorder requires a tab index.");
  }
  const groupId = findGroupInNode(state.root, firstId)
    ? firstId
    : findTabGroupInNode(state.root, second);
  if (groupId === undefined) throw new RangeError(`Tab is not open: ${second}.`);
  return {
    groupId,
    tabId: findGroupInNode(state.root, firstId) ? second : firstId,
    index: integerIndex(third, "Tab index")
  };
}

/** Atomically reorder a tab within its owning group. */
export function reorderTab(
  state: EditorLayoutState,
  tabId: string,
  index: number
): EditorLayoutState;
export function reorderTab(
  state: EditorLayoutState,
  groupId: string,
  tabId: string,
  index: number
): EditorLayoutState;
export function reorderTab(
  state: EditorLayoutState,
  options: ReorderTabOptions
): EditorLayoutState;
export function reorderTab(
  state: EditorLayoutState,
  first: string | ReorderTabOptions,
  second?: string | number,
  third?: number
): EditorLayoutState {
  assertLayoutValue(state);
  const resolved = resolveReorder(state, first, second, third);
  const group = requireGroup(state, resolved.groupId);
  const sourceIndex = group.tabs.indexOf(resolved.tabId);
  if (sourceIndex < 0) {
    throw new RangeError("Tab does not belong to the requested group.");
  }
  const tabs = group.tabs.filter((tabId) => tabId !== resolved.tabId);
  const insertionIndex = clampInsertionIndex(resolved.index, tabs.length);
  tabs.splice(insertionIndex, 0, resolved.tabId);
  const changed = tabs.some((tabId, index) => tabId !== group.tabs[index]);
  if (!changed) {
    return state.focusedGroupId === group.id ? state : makeState(state.root, group.id);
  }
  const [root] = replaceNode(state.root, group.id, (node) => {
    if (node.kind !== "group") return node;
    return makeGroup(node.id, tabs, node.activeTabId);
  });
  return stateWithRoot(state, root, group.id);
}

export const reorderEditorTab = reorderTab;

/** Reorder relative to another tab, preserving the source group. */
export function reorderTabRelative(
  state: EditorLayoutState,
  tabId: string,
  targetTabId: string,
  side: "before" | "after",
  groupId?: string
): EditorLayoutState {
  assertLayoutValue(state);
  const sourceId = requireTabId(tabId);
  const targetId = requireTabId(targetTabId);
  if (side !== "before" && side !== "after") {
    throw new TypeError("Tab drop side must be before or after.");
  }
  const owner = groupId ?? findTabGroupInNode(state.root, sourceId);
  if (owner === undefined) throw new RangeError(`Tab is not open: ${sourceId}.`);
  const group = requireGroup(state, owner);
  if (!group.tabs.includes(sourceId) || !group.tabs.includes(targetId)) {
    throw new RangeError("Relative reorder tabs must share an editor group.");
  }
  if (sourceId === targetId) return state;
  const remaining = group.tabs.filter((id) => id !== sourceId);
  const targetIndex = remaining.indexOf(targetId);
  if (targetIndex < 0) throw new RangeError("Relative reorder target is missing.");
  return reorderTab(state, owner, sourceId, targetIndex + (side === "after" ? 1 : 0));
}

function resolveMove(
  state: EditorLayoutState,
  first: string | MoveTabOptions,
  second: string | undefined,
  third: number | undefined
): MoveTabOptions {
  if (isRecord(first)) {
    return {
      tabId: requireTabId(first.tabId),
      targetGroupId: requiredId(first.targetGroupId, "Target group ID"),
      targetIndex: first.targetIndex === undefined
        ? undefined
        : integerIndex(first.targetIndex, "Target tab index")
    };
  }
  if (second === undefined) {
    throw new TypeError("Move requires a target group.");
  }
  return {
    tabId: requireTabId(first),
    targetGroupId: requiredId(second, "Target group ID"),
    targetIndex: third === undefined ? undefined : integerIndex(third, "Target tab index")
  };
}

/** Move a tab exactly once to a group and activate/focus the destination. */
export function moveTab(
  state: EditorLayoutState,
  tabId: string,
  targetGroupId: string,
  targetIndex?: number
): EditorLayoutState;
export function moveTab(
  state: EditorLayoutState,
  options: MoveTabOptions
): EditorLayoutState;
export function moveTab(
  state: EditorLayoutState,
  first: string | MoveTabOptions,
  second?: string,
  third?: number
): EditorLayoutState {
  assertLayoutValue(state);
  const move = resolveMove(state, first, second, third);
  const sourceGroupId = findTabGroupInNode(state.root, move.tabId);
  if (sourceGroupId === undefined) throw new RangeError(`Tab is not open: ${move.tabId}.`);
  const source = requireGroup(state, sourceGroupId);
  const target = requireGroup(state, move.targetGroupId);
  const sourceIndex = source.tabs.indexOf(move.tabId);

  if (source.id === target.id) {
    if (move.targetIndex === undefined) {
      const [root] = replaceNode(state.root, target.id, (node) => {
        if (node.kind !== "group") return node;
        return makeGroup(node.id, node.tabs, move.tabId);
      });
      return stateWithRoot(state, root, target.id);
    }
    const remaining = source.tabs.filter((id) => id !== move.tabId);
    const insertion = clampInsertionIndex(move.targetIndex, remaining.length);
    remaining.splice(insertion, 0, move.tabId);
    const [root] = replaceNode(state.root, source.id, (node) => {
      if (node.kind !== "group") return node;
      return makeGroup(node.id, remaining, move.tabId);
    });
    return stateWithRoot(state, root, target.id);
  }

  const sourceTabs = source.tabs.filter((id) => id !== move.tabId);
  const sourceActive = source.activeTabId === move.tabId
    ? fallbackAfterRemoval(source.tabs, sourceIndex)
    : source.activeTabId;
  const targetTabs = [...target.tabs];
  const targetIndex = clampInsertionIndex(
    move.targetIndex === undefined ? targetTabs.length : move.targetIndex,
    targetTabs.length
  );
  targetTabs.splice(targetIndex, 0, move.tabId);
  const updates = new Map<string, EditorGroupNode>([
    [source.id, makeGroup(source.id, sourceTabs, sourceActive)],
    [target.id, makeGroup(target.id, targetTabs, move.tabId)]
  ]);
  const [root] = replaceGroups(state.root, updates);
  return stateWithRoot(state, root, target.id);
}

export const moveEditorTab = moveTab;
export const moveTabToGroup = moveTab;

function payloadFromInput(
  first: string | Pick<EditorTabDragPayload, "tabId" | "sourceGroupId">,
  second?: string
): EditorTabDragPayload {
  const tabId = typeof first === "string" ? first : first.tabId;
  const sourceGroupId = typeof first === "string" ? second : first.sourceGroupId;
  requiredId(tabId, "Dragged tab ID");
  const normalizedSourceGroupId = requiredId(
    sourceGroupId,
    "Dragged source group ID"
  );
  return Object.freeze({
    version: EDITOR_TAB_DRAG_VERSION,
    tabId,
    sourceGroupId: normalizedSourceGroupId
  });
}

/** Create a typed application-specific drag payload. */
export function createEditorTabDragPayload(
  tabId: string,
  sourceGroupId: string
): EditorTabDragPayload;
export function createEditorTabDragPayload(
  payload: Pick<EditorTabDragPayload, "tabId" | "sourceGroupId">
): EditorTabDragPayload;
export function createEditorTabDragPayload(
  first: string | Pick<EditorTabDragPayload, "tabId" | "sourceGroupId">,
  second?: string
): EditorTabDragPayload {
  return payloadFromInput(first, second);
}

export function encodeEditorTabDragPayload(
  payload: EditorTabDragPayload | Pick<EditorTabDragPayload, "tabId" | "sourceGroupId">
): string {
  const normalized = payloadFromInput(payload, undefined);
  return JSON.stringify(normalized);
}

export const serializeEditorTabDragPayload = encodeEditorTabDragPayload;

function decodeDragValue(value: unknown): EditorTabDragPayload | null {
  const candidate = typeof value === "string"
    ? (() => {
        try {
          return JSON.parse(value) as unknown;
        } catch {
          return undefined;
        }
      })()
    : value;
  if (!isRecord(candidate)) return null;
  if (candidate.version !== EDITOR_TAB_DRAG_VERSION) return null;
  if (typeof candidate.tabId !== "string" || candidate.tabId.length === 0) return null;
  if (
    typeof candidate.sourceGroupId !== "string" ||
    candidate.sourceGroupId.length === 0
  ) {
    return null;
  }
  const keys = Object.keys(candidate);
  if (
    keys.length !== 3 ||
    !keys.includes("version") ||
    !keys.includes("tabId") ||
    !keys.includes("sourceGroupId")
  ) {
    return null;
  }
  return Object.freeze({
    version: EDITOR_TAB_DRAG_VERSION,
    tabId: candidate.tabId,
    sourceGroupId: candidate.sourceGroupId
  });
}

/** Parse and strictly validate an encoded or already-decoded drag payload. */
export function parseEditorTabDragPayload(
  value: unknown
): EditorTabDragPayload | null {
  return decodeDragValue(value);
}

export const decodeEditorTabDragPayload = parseEditorTabDragPayload;

export function isEditorTabDragPayload(
  value: unknown
): value is EditorTabDragPayload {
  return decodeDragValue(value) !== null;
}

interface DataTransferLike {
  setData(type: string, data: string): void;
  getData(type: string): string;
  effectAllowed?: string;
}

export function writeEditorTabDragPayload(
  dataTransfer: Pick<DataTransferLike, "setData"> & Partial<Pick<DataTransferLike, "effectAllowed">>,
  payload: EditorTabDragPayload | Pick<EditorTabDragPayload, "tabId" | "sourceGroupId">
): void {
  dataTransfer.setData(EDITOR_TAB_DRAG_MIME, encodeEditorTabDragPayload(payload));
  if ("effectAllowed" in dataTransfer) dataTransfer.effectAllowed = "move";
}

export function readEditorTabDragPayload(
  dataTransfer: Pick<DataTransferLike, "getData">
): EditorTabDragPayload | null {
  try {
    return parseEditorTabDragPayload(dataTransfer.getData(EDITOR_TAB_DRAG_MIME));
  } catch {
    return null;
  }
}

function payloadFromDropInput(value: unknown): EditorTabDragPayload | null {
  if (isRecord(value) && typeof value.getData === "function") {
    return readEditorTabDragPayload(value as unknown as Pick<DataTransferLike, "getData">);
  }
  return parseEditorTabDragPayload(value);
}

/**
 * Apply a validated drop.  Malformed, external, stale, or ownership-mismatched
 * payloads are ignored and return the original state unchanged.
 */
export function dropEditorTab(
  state: EditorLayoutState,
  payload: unknown,
  targetGroupId: string,
  targetIndex?: number
): EditorLayoutState {
  assertLayoutValue(state);
  const decoded = payloadFromDropInput(payload);
  if (!decoded) return state;
  if (typeof targetGroupId !== "string" || targetGroupId.length === 0) return state;
  if (targetIndex !== undefined && !Number.isInteger(targetIndex)) return state;
  const target = findGroupInNode(state.root, targetGroupId);
  const source = findTabGroupInNode(state.root, decoded.tabId);
  if (!target || source !== decoded.sourceGroupId) return state;
  return moveTab(state, decoded.tabId, targetGroupId, targetIndex);
}

export const applyEditorTabDrop = dropEditorTab;
export const moveEditorTabFromDrag = dropEditorTab;

/** Apply a drop before or after a populated target tab. */
export function dropEditorTabRelative(
  state: EditorLayoutState,
  payload: unknown,
  targetGroupId: string,
  targetTabId: string,
  side: "before" | "after"
): EditorLayoutState {
  assertLayoutValue(state);
  const decoded = payloadFromDropInput(payload);
  if (!decoded || (side !== "before" && side !== "after")) return state;
  const target = findGroupInNode(state.root, targetGroupId);
  if (!target || !target.tabs.includes(targetTabId)) return state;
  const source = findTabGroupInNode(state.root, decoded.tabId);
  if (source !== decoded.sourceGroupId) return state;
  const targetTabs = source === targetGroupId
    ? target.tabs.filter((id) => id !== decoded.tabId)
    : target.tabs;
  const targetIndex = targetTabs.indexOf(targetTabId);
  if (targetIndex < 0) return state;
  return moveTab(
    state,
    decoded.tabId,
    targetGroupId,
    targetIndex + (side === "after" ? 1 : 0)
  );
}
